import { __assign } from '../../../_virtual/_tslib.js';
import { DEFAULT_POST_TYPE_ID, POST_TYPE_IDS_ENUM } from '../../../constants/BrandedCommunity/Post.js';
import { useDocInEditPostModal } from './useDocInEditPostModal.js';
import { useImageInEditPostModal } from './useImageInEditPostModal.js';
import { useOfferingInEditPostModal } from './useOfferingInEditPostModal.js';
import { useVideoInEditPostModal } from './useVideoInEditPostModal.js';
import { useRef, useState, useEffect } from 'react';
import { POST_SHARING_TYPES } from '../../../features/BrandedCommunity/constants/BrandedCommunity.constants.js';
import { checkIsDefined } from '../../../features/Common/modules/DataTypes/utils/nanNullUndef.js';

var POST_UPDATE_PAYLOAD_DEFAULT_VALUE = {
    post_heading: "",
    post_body: "",
    post_type: DEFAULT_POST_TYPE_ID,
    metadata: {
        auto_expand: true,
    },
};
var CREATOR_POST_UPDATE_PAYLOAD_DEFAULT_VALUE = __assign(__assign({}, POST_UPDATE_PAYLOAD_DEFAULT_VALUE), { 
    // creator is allowed to change the post sharing type and customer's post sharing type will be determined by the creator in channel's settings
    metadata: __assign(__assign({}, POST_UPDATE_PAYLOAD_DEFAULT_VALUE.metadata), { post_sharing_type: POST_SHARING_TYPES.PUBLIC }) });
var useEditPostModal = function (_a) {
    var _b;
    var isOpen = _a.isOpen, isCreator = _a.isCreator, onClose = _a.onClose, postDataProp = _a.postData, disablePostTypeChangeProp = _a.disablePostTypeChange, disablePostTypeDataEdit = _a.disablePostTypeDataEdit, triggerPostTypeIdProp = _a.triggerPostTypeId, onSaveButtonClickProp = _a.onSaveButtonClick, onPostOfferingBuyNowClick = _a.onPostOfferingBuyNowClick, notifyError = _a.notifyError, customCheckPostContentIsNotEmpty = _a.customCheckPostContentIsNotEmpty, onEditPostTypeMediaClick = _a.onEditPostTypeMediaClick;
    var postContentTextAreaRef = useRef(null);
    /**
     * This value tells the triggered post type id.
     * If value is set, the respective post type child modal will open initially.
     * If the child modal is closed directly the base modal(edit post modal) will be closed as well.
     */
    var _c = useState(undefined), triggerPostTypeId = _c[0], setTriggerPostTypeId = _c[1];
    var onCloseSelectorWithoutSelecting = function () {
        if ([
            POST_TYPE_IDS_ENUM.IMAGE,
            POST_TYPE_IDS_ENUM.VIDEO,
            POST_TYPE_IDS_ENUM.DOCUMENT,
            POST_TYPE_IDS_ENUM.OFFERING,
        ].includes(triggerPostTypeId)) {
            onClose();
        }
    };
    var onAddPostTypeMediaSuccess = function () {
        // so that further editing of post type media does not trigger the onClose of the EditPostModal
        if (typeof triggerPostTypeId === "number") {
            setTriggerPostTypeId(undefined);
        }
    };
    var _d = useState(postDataProp ||
        (isCreator
            ? CREATOR_POST_UPDATE_PAYLOAD_DEFAULT_VALUE
            : POST_UPDATE_PAYLOAD_DEFAULT_VALUE)), postData = _d[0], setPostData = _d[1];
    var onSaveButtonClick = function () {
        var postContent = "";
        if (postContentTextAreaRef === null || postContentTextAreaRef === void 0 ? void 0 : postContentTextAreaRef.current) {
            postContent = postContentTextAreaRef.current.value || "";
        }
        if (postTypeId === POST_TYPE_IDS_ENUM.TEXT &&
            (customCheckPostContentIsNotEmpty
                ? !customCheckPostContentIsNotEmpty(postContent)
                : !postContent.trim())) {
            notifyError("Post connot be empty!");
            return;
        }
        var payload = __assign(__assign({}, postData), { post_body: postContent });
        delete payload.listing_details; // we get listing details in the get api for rendering at front end... its not actually a part of the post data
        onSaveButtonClickProp({ payload: payload });
    };
    var resetPostTypeToText = function () {
        setPostData(function (prev) {
            var _a, _b, _c, _d;
            return (__assign(__assign({}, prev), { post_type: DEFAULT_POST_TYPE_ID, post_listing_uuid: undefined, listing_details: undefined, metadata: __assign({ auto_expand: (_b = (_a = prev.metadata) === null || _a === void 0 ? void 0 : _a.auto_expand) !== null && _b !== void 0 ? _b : true }, (checkIsDefined((_c = prev === null || prev === void 0 ? void 0 : prev.metadata) === null || _c === void 0 ? void 0 : _c.post_sharing_type)
                    ? { post_sharing_type: (_d = prev.metadata) === null || _d === void 0 ? void 0 : _d.post_sharing_type }
                    : undefined)) }));
        });
    };
    var onRemovePostTypeMedia = resetPostTypeToText;
    var _e = useImageInEditPostModal({
        postData: postData,
        setPostData: setPostData,
        disablePostTypeDataEdit: disablePostTypeDataEdit,
        onRemovePostTypeMedia: onRemovePostTypeMedia,
        onCloseSelectorWithoutSelecting: onCloseSelectorWithoutSelecting,
        onAddPostTypeMediaSuccess: onAddPostTypeMediaSuccess,
        onEditPostTypeMediaClick: onEditPostTypeMediaClick,
    }), onAddPostTypeImageClick = _e.onAddPostTypeImageClick, postImageProps = _e.postImageProps, imageSelectorProps = _e.imageSelectorProps;
    var _f = useDocInEditPostModal({
        postData: postData,
        setPostData: setPostData,
        disablePostTypeDataEdit: disablePostTypeDataEdit,
        onRemovePostTypeMedia: onRemovePostTypeMedia,
        onCloseSelectorWithoutSelecting: onCloseSelectorWithoutSelecting,
        onAddPostTypeMediaSuccess: onAddPostTypeMediaSuccess,
        onEditPostTypeMediaClick: onEditPostTypeMediaClick,
    }), onAddPostTypeDocClick = _f.onAddPostTypeDocClick, postDocProps = _f.postDocProps, docSelectorProps = _f.docSelectorProps;
    var _g = useVideoInEditPostModal({
        postData: postData,
        setPostData: setPostData,
        disablePostTypeDataEdit: disablePostTypeDataEdit,
        onRemovePostTypeMedia: onRemovePostTypeMedia,
        onCloseSelectorWithoutSelecting: onCloseSelectorWithoutSelecting,
        onAddPostTypeMediaSuccess: onAddPostTypeMediaSuccess,
        onEditPostTypeMediaClick: onEditPostTypeMediaClick,
    }), onAddPostTypeVideoClick = _g.onAddPostTypeVideoClick, postVideoProps = _g.postVideoProps, videoSelectorProps = _g.videoSelectorProps;
    var _h = useOfferingInEditPostModal({
        postData: postData,
        setPostData: setPostData,
        disablePostTypeDataEdit: disablePostTypeDataEdit,
        onRemovePostTypeMedia: onRemovePostTypeMedia,
        onCloseSelectorWithoutSelecting: onCloseSelectorWithoutSelecting,
        onAddPostTypeMediaSuccess: onAddPostTypeMediaSuccess,
        onPostOfferingBuyNowClick: onPostOfferingBuyNowClick,
        onEditPostTypeMediaClick: onEditPostTypeMediaClick,
    }), onAddPostTypeOfferingClick = _h.onAddPostTypeOfferingClick, postOfferingProps = _h.postOfferingProps, offeringSelectorProps = _h.offeringSelectorProps;
    var postTypeId = postData.post_type;
    var postMediaUploading = imageSelectorProps.imgUploading ||
        videoSelectorProps.videoUploading ||
        docSelectorProps.docUploading ||
        // obviously, uploading does not happen in case of offering
        false;
    var postMediaIsAdded = false;
    var disablePostTypeChange = false;
    if (!postMediaUploading) {
        switch (postTypeId) {
            case POST_TYPE_IDS_ENUM.IMAGE:
                postMediaIsAdded = postImageProps.showImages;
                break;
            case POST_TYPE_IDS_ENUM.DOCUMENT:
                postMediaIsAdded = postDocProps.showDocs;
                break;
            case POST_TYPE_IDS_ENUM.VIDEO:
                postMediaIsAdded = postVideoProps.showVideo;
                disablePostTypeChange = true;
                break;
            case POST_TYPE_IDS_ENUM.OFFERING:
                postMediaIsAdded = postOfferingProps.showOffering;
                disablePostTypeChange = true;
                break;
            case POST_TYPE_IDS_ENUM.TEXT:
        }
    }
    var disablePostTypeClick = disablePostTypeChangeProp || disablePostTypeChange || postMediaUploading;
    var onAddPostTypeMediaClick = function (postTypeIdArg) {
        if (disablePostTypeClick)
            return;
        switch (postTypeIdArg) {
            case POST_TYPE_IDS_ENUM.IMAGE:
                onAddPostTypeImageClick();
                break;
            case POST_TYPE_IDS_ENUM.DOCUMENT:
                onAddPostTypeDocClick();
                break;
            case POST_TYPE_IDS_ENUM.VIDEO:
                onAddPostTypeVideoClick();
                break;
            case POST_TYPE_IDS_ENUM.OFFERING:
                onAddPostTypeOfferingClick();
                break;
            case POST_TYPE_IDS_ENUM.TEXT:
            default:
                resetPostTypeToText();
                break;
        }
    };
    var handleChangeSharingType = function (sharingType) {
        setPostData(function (prev) { return (__assign(__assign({}, prev), { metadata: __assign(__assign({}, prev === null || prev === void 0 ? void 0 : prev.metadata), { post_sharing_type: sharingType }) })); });
    };
    var handleChangeAutoExpand = function (checked) {
        setPostData(__assign(__assign({}, postData), { metadata: __assign(__assign({}, postData.metadata), { auto_expand: checked }) }));
    };
    useEffect(function () {
        if (isOpen) {
            if (typeof triggerPostTypeIdProp === "number") {
                setTriggerPostTypeId(triggerPostTypeIdProp);
                onAddPostTypeMediaClick(triggerPostTypeIdProp);
            }
        }
        else {
            setPostData(isCreator
                ? CREATOR_POST_UPDATE_PAYLOAD_DEFAULT_VALUE
                : POST_UPDATE_PAYLOAD_DEFAULT_VALUE);
            setTriggerPostTypeId(undefined);
            if (postContentTextAreaRef.current) {
                postContentTextAreaRef.current.value = "";
            }
        }
    }, [isOpen]);
    return {
        // props used by the modal directly
        isOpen: isOpen,
        onClose: onClose,
        postContentDefaultValue: postData.post_body,
        postContentTextAreaRef: postContentTextAreaRef,
        postMediaUploading: postMediaUploading,
        postMediaIsAdded: postMediaIsAdded,
        postImageProps: postImageProps,
        postDocProps: postDocProps,
        postVideoProps: postVideoProps,
        postOfferingProps: postOfferingProps,
        // props used by other components related to modal
        postData: postData,
        onSaveButtonClick: onSaveButtonClick,
        onChangeSharingType: handleChangeSharingType,
        postTypeSelectorProps: {
            onPostTypeClick: onAddPostTypeMediaClick,
            disablePostTypeClick: disablePostTypeClick,
            postTypeId: postTypeId,
            postMediaIsAdded: postMediaIsAdded,
        },
        autoExpandPostComponentProps: {
            onChange: handleChangeAutoExpand,
            value: !!((_b = postData.metadata) === null || _b === void 0 ? void 0 : _b.auto_expand),
        },
        imageSelectorProps: imageSelectorProps,
        docSelectorProps: docSelectorProps,
        videoSelectorProps: videoSelectorProps,
        offeringSelectorProps: offeringSelectorProps,
    };
};

export { useEditPostModal };
//# sourceMappingURL=useEditPostModal.js.map
